import React, { useMemo, useState } from "react";
import { Container, LoginButton } from "../../styles/Styles";
import AdminNav from "./AdminNav";
import { useMutation, useQuery } from "@apollo/client";
import { GET_GUIDE_REVIEWS, DELETE_GUIDE_REVIEW } from "../gql/guide-reviews-gql";
import { GET_GUIDES_LIST } from "../gql/guides-gql";
import ReviewMonthCalendar from "../common/ReviewMonthCalendar";
import GuideReviewImage from "../common/GuideReviewImage";
import { uploadGuideReview } from "../common/uploadGuideReview";
import { GuideReview } from "../common/interfaces";
import Loader from "../common/Loader";
import { toast } from "react-toastify";
import { useAuth } from "../../context/AuthContext";
import { Roles } from "../common/roles";
import { Navigate } from "react-router-dom";
import styled from "styled-components";
import moment from "moment/moment";

const Section = styled.div`
    background: white;
    padding: 1.5rem;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    margin-bottom: 2rem;
`;

const FormGroup = styled.div`
    margin-bottom: 1rem;
`;

const Label = styled.label`
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: #333;
`;

const Select = styled.select`
    width: 100%;
    max-width: 400px;
    padding: 0.8rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 1rem;
`;

const Input = styled.input`
    width: 100%;
    max-width: 400px;
    padding: 0.8rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 1rem;
`;

const GuideSection = styled.div`
    margin-bottom: 2rem;
    padding-bottom: 1.5rem;
    border-bottom: 1px solid #eee;

    &:last-child {
        border-bottom: none;
        margin-bottom: 0;
        padding-bottom: 0;
    }
`;

const GuideHeading = styled.h3`
    margin: 0 0 1rem 0;
    color: #333;
`;

const ReviewGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 1rem;
`;

const ReviewItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`;

const ReviewMeta = styled.span`
    font-size: 0.85rem;
    color: #666;
`;

const groupReviewsByGuide = (reviews: GuideReview[]) => {
    const groups = new Map<string, { guideName: string; reviews: GuideReview[] }>();

    reviews.forEach((review) => {
        const guideId = review.guide._id;
        const guideName = `${review.guide.profile.firstName} ${review.guide.profile.lastName}`;
        const existing = groups.get(guideId);

        if (existing) {
            existing.reviews.push(review);
        } else {
            groups.set(guideId, { guideName, reviews: [review] });
        }
    });

    return Array.from(groups.values()).sort((a, b) =>
        a.guideName.localeCompare(b.guideName)
    );
};

const AdminDashboardReviews = () => {
    const { user, isAuthenticated } = useAuth();
    const [currentMonth, setCurrentMonth] = useState(() => new Date());
    const [selectedGuideId, setSelectedGuideId] = useState("");
    const [formMonth, setFormMonth] = useState(currentMonth.getMonth() + 1);
    const [formYear, setFormYear] = useState(currentMonth.getFullYear());
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [fileInputKey, setFileInputKey] = useState(0);
    const [uploading, setUploading] = useState(false);

    const queryVars = {
        month: currentMonth.getMonth() + 1,
        year: currentMonth.getFullYear(),
    };

    const { data, loading, refetch } = useQuery(GET_GUIDE_REVIEWS, {
        variables: queryVars,
        skip: !isAuthenticated,
    });

    const { data: guidesList, loading: guidesLoading } = useQuery(GET_GUIDES_LIST);

    const [deleteGuideReview] = useMutation(DELETE_GUIDE_REVIEW, {
        onCompleted: () => {
            toast.success("Review deleted successfully!");
            refetch();
        },
        onError: (error) => {
            toast.error("Failed to delete review: " + error.message);
        },
    });

    const groupedReviews = useMemo(() => {
        const reviews: GuideReview[] = data?.getGuideReviews || [];
        return groupReviewsByGuide(reviews);
    }, [data?.getGuideReviews]);
    const guides = guidesList?.getGuidesList || [];

    const resetReviewForm = (month: number, year: number) => {
        setSelectedGuideId("");
        setSelectedFile(null);
        setFileInputKey((prev) => prev + 1);
        setFormMonth(month);
        setFormYear(year);
    };

    const handleMonthChange = (date: Date) => {
        setCurrentMonth(date);
        setFormMonth(date.getMonth() + 1);
        setFormYear(date.getFullYear());
    };

    const handleUpload = async (event: React.FormEvent) => {
        event.preventDefault();

        if (!selectedGuideId) {
            toast.warning("Please select a guide");
            return;
        }

        if (!selectedFile) {
            toast.warning("Please select a screenshot to upload");
            return;
        }

        setUploading(true);

        const uploadedMonth = formMonth;
        const uploadedYear = formYear;

        try {
            await uploadGuideReview({
                file: selectedFile,
                guideId: selectedGuideId,
                month: uploadedMonth,
                year: uploadedYear,
            });
            toast.success("Review uploaded successfully!");

            const uploadedDate = new Date(uploadedYear, uploadedMonth - 1, 1);
            setCurrentMonth(uploadedDate);
            resetReviewForm(uploadedMonth, uploadedYear);

            await refetch({
                month: uploadedMonth,
                year: uploadedYear,
            });
        } catch (error: any) {
            toast.error(error?.response?.data?.message || error?.message || "Failed to upload review");
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (reviewId: string) => {
        if (!window.confirm("Are you sure you want to delete this review?")) {
            return;
        }

        await deleteGuideReview({
            variables: { id: reviewId },
        });
    };

    if (!isAuthenticated || user?.role[0] !== Roles.ADMIN) {
        return <Navigate to="/" />;
    }

    if (loading || guidesLoading) {
        return <Loader />;
    }

    return (
        <Container>
            <AdminNav />
            <h2>Reviews</h2>

            <ReviewMonthCalendar
                currentDate={currentMonth}
                onMonthChange={handleMonthChange}
            />

            <Section>
                <h3>Add Review</h3>
                <form onSubmit={handleUpload}>
                    <FormGroup>
                        <Label htmlFor="guideId">Guide</Label>
                        <Select
                            id="guideId"
                            value={selectedGuideId}
                            onChange={(e) => setSelectedGuideId(e.target.value)}
                        >
                            <option value="">Select a guide</option>
                            {guides.map((guide: any) => (
                                <option key={guide._id} value={guide._id}>
                                    {guide.profile.firstName} {guide.profile.lastName}
                                </option>
                            ))}
                        </Select>
                    </FormGroup>

                    <FormGroup>
                        <Label htmlFor="month">Month</Label>
                        <Select
                            id="month"
                            value={formMonth}
                            onChange={(e) => setFormMonth(Number(e.target.value))}
                        >
                            {Array.from({ length: 12 }, (_, index) => (
                                <option key={index + 1} value={index + 1}>
                                    {moment().month(index).format("MMMM")}
                                </option>
                            ))}
                        </Select>
                    </FormGroup>

                    <FormGroup>
                        <Label htmlFor="year">Year</Label>
                        <Input
                            id="year"
                            type="number"
                            min={2000}
                            max={2100}
                            value={formYear}
                            onChange={(e) => setFormYear(Number(e.target.value))}
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label htmlFor="file">Screenshot</Label>
                        <Input
                            key={fileInputKey}
                            id="file"
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            onChange={(e) => setSelectedFile(e.target.files?.[0] ?? null)}
                        />
                    </FormGroup>

                    <LoginButton type="submit" disabled={uploading}>
                        {uploading ? "Uploading..." : "Upload Review"}
                    </LoginButton>
                </form>
            </Section>

            <Section>
                <h3>Reviews for {moment(currentMonth).format("MMMM YYYY")}</h3>
                {groupedReviews.length === 0 ? (
                    <p>No reviews for this month.</p>
                ) : (
                    groupedReviews.map(({ guideName, reviews: guideReviews }) => (
                        <GuideSection key={guideName}>
                            <GuideHeading>{guideName}</GuideHeading>
                            <ReviewGrid>
                                {guideReviews.map((review) => (
                                    <ReviewItem key={review._id}>
                                        <GuideReviewImage
                                            reviewId={review._id}
                                            alt={`Review for ${guideName}`}
                                            onDelete={handleDelete}
                                        />
                                        <ReviewMeta>
                                            Added {moment(review.createdAt).format("D MMM YYYY h:mm a")}
                                        </ReviewMeta>
                                    </ReviewItem>
                                ))}
                            </ReviewGrid>
                        </GuideSection>
                    ))
                )}
            </Section>
        </Container>
    );
};

export default AdminDashboardReviews;
